import { useState } from "react";
import type { EventType, Person } from "@shared/types";
import { Modal } from "./ui/Modal";
import { Button } from "./ui/Button";
import { eventTypesApi, peopleApi } from "@/api/client";

interface SettingsPanelProps {
  open: boolean;
  people: Person[];
  eventTypes: EventType[];
  onClose: () => void;
  onChanged: () => void;
}

const SWATCHES = [
  "#1E3A8A", "#9A1B2F", "#2563EB", "#DC2626", "#D97706", "#7C3AED",
  "#0EA5E9", "#059669", "#334155", "#6B7280", "#DB2777", "#65A30D",
];

function ColorPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {SWATCHES.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className="h-6 w-6 rounded-full ring-offset-2 ring-offset-surface dark:ring-offset-surface-dark"
          style={{ backgroundColor: c, boxShadow: value === c ? `0 0 0 2px ${c}` : undefined }}
          aria-label={c}
        />
      ))}
    </div>
  );
}

export function SettingsPanel({ open, people, eventTypes, onClose, onChanged }: SettingsPanelProps) {
  const [tab, setTab] = useState<"people" | "types">("people");
  const [newPersonName, setNewPersonName] = useState("");
  const [newPersonColor, setNewPersonColor] = useState(SWATCHES[0]);
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeColor, setNewTypeColor] = useState(SWATCHES[2]);
  const [busy, setBusy] = useState(false);

  async function addPerson() {
    if (!newPersonName.trim()) return;
    setBusy(true);
    try {
      await peopleApi.create({ name: newPersonName.trim(), color: newPersonColor });
      setNewPersonName("");
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function addType() {
    if (!newTypeName.trim()) return;
    setBusy(true);
    try {
      await eventTypesApi.create({ name: newTypeName.trim(), color: newTypeColor });
      setNewTypeName("");
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} width="md">
      <div className="flex max-h-[85vh] flex-col">
        <div className="flex items-center justify-between border-b border-line dark:border-line-dark px-6 py-4">
          <h2 className="text-lg font-semibold">Settings</h2>
          <button onClick={onClose} aria-label="Close" className="rounded-full p-1.5 hover:bg-black/5 dark:hover:bg-white/10">
            ✕
          </button>
        </div>

        <div className="flex gap-1 border-b border-line dark:border-line-dark px-6 pt-3">
          {(["people", "types"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-t-lg px-3 py-2 text-sm font-medium ${tab === t ? "border-b-2 border-accent text-accent" : "text-muted dark:text-muted-dark"}`}
            >
              {t === "people" ? "People" : "Event types"}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {tab === "people" ? (
            <div className="space-y-3">
              {people.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-lg border border-line dark:border-line-dark px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: p.color }} />
                    <span className={`text-sm font-medium ${p.archived ? "line-through text-muted dark:text-muted-dark" : ""}`}>
                      {p.name}
                    </span>
                  </div>
                  <button
                    onClick={async () => {
                      await peopleApi.update(p.id, { archived: !p.archived });
                      onChanged();
                    }}
                    className="text-xs font-medium text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark"
                  >
                    {p.archived ? "Restore" : "Archive"}
                  </button>
                </div>
              ))}

              <div className="rounded-lg border border-dashed border-line dark:border-line-dark p-3">
                <p className="mb-2 text-xs font-medium text-muted dark:text-muted-dark">Add a person</p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={newPersonName}
                    onChange={(e) => setNewPersonName(e.target.value)}
                    placeholder="Name"
                    className="min-w-[8rem] flex-1 rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-1.5 text-sm"
                  />
                  <ColorPicker value={newPersonColor} onChange={setNewPersonColor} />
                  <Button size="sm" variant="primary" onClick={addPerson} disabled={busy}>
                    Add
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {eventTypes.map((t) => (
                <div key={t.id} className="flex items-center justify-between rounded-lg border border-line dark:border-line-dark px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: t.color }} />
                    <span className={`text-sm font-medium ${t.archived ? "line-through text-muted dark:text-muted-dark" : ""}`}>
                      {t.name}
                    </span>
                  </div>
                  <button
                    onClick={async () => {
                      await eventTypesApi.update(t.id, { archived: !t.archived });
                      onChanged();
                    }}
                    className="text-xs font-medium text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark"
                  >
                    {t.archived ? "Restore" : "Archive"}
                  </button>
                </div>
              ))}

              <div className="rounded-lg border border-dashed border-line dark:border-line-dark p-3">
                <p className="mb-2 text-xs font-medium text-muted dark:text-muted-dark">Add an event type</p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={newTypeName}
                    onChange={(e) => setNewTypeName(e.target.value)}
                    placeholder="Name"
                    className="min-w-[8rem] flex-1 rounded-lg border border-line dark:border-line-dark bg-transparent px-3 py-1.5 text-sm"
                  />
                  <ColorPicker value={newTypeColor} onChange={setNewTypeColor} />
                  <Button size="sm" variant="primary" onClick={addType} disabled={busy}>
                    Add
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
